import { useEffect } from 'react';

const SKIP_CLASS = /fix-tab|header-user|amz-member|member-day/i;
const NESTED_SECTION = '[class*="vanlife-roadtrips-"], [class*="outdoor-recreation-"]';

function shouldObserve(el: Element) {
    if (!(el instanceof HTMLElement)) return false;
    if (['STYLE', 'SCRIPT', 'NOSCRIPT'].includes(el.tagName)) return false;
    const cls = typeof el.className === 'string' ? el.className : '';
    return !SKIP_CLASS.test(cls);
}

function reveal(el: Element) {
    el.classList.add('is-reveal');
    el.querySelectorAll('.ani-1, .ani-2, .ani-3, .ani-4').forEach((node) => node.classList.add('enter'));
    el.querySelectorAll(NESTED_SECTION).forEach((child) => {
        child.classList.add('is-reveal');
        child.querySelectorAll('.ani-1, .ani-2, .ani-3, .ani-4').forEach((node) => node.classList.add('enter'));
    });
}

export function useSectionReveal(rootSelector = '.outdoor-recreation') {
    useEffect(() => {
        const root = document.querySelector(rootSelector);
        if (!root) return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            Array.from(root.children).filter(shouldObserve).forEach(reveal);
            return;
        }

        const observed = new WeakSet<Element>();
        const vh = window.innerHeight * 0.2;
        const io =
            'IntersectionObserver' in window
                ? new IntersectionObserver(
                      (entries) => {
                          entries.forEach((entry) => {
                              if (!entry.isIntersecting) return;
                              reveal(entry.target);
                              io?.unobserve(entry.target);
                          });
                      },
                      { rootMargin: `0px 0px -${vh}px 0px`, threshold: 0 },
                  )
                : null;

        const scan = () => {
            Array.from(root.children).forEach((el) => {
                if (!shouldObserve(el) || observed.has(el)) return;
                observed.add(el);
                if (io) {
                    io.observe(el);
                } else {
                    reveal(el);
                }
            });
        };

        scan();
        const mo = new MutationObserver(scan);
        mo.observe(root, { childList: true });

        return () => {
            mo.disconnect();
            io?.disconnect();
        };
    }, [rootSelector]);
}
